// src/services/mockApi.ts
import { User, Department, Task } from '../types';

// Re-export User, Department, and Task for other modules to use
export type { User, Department, Task };

export const getCompanyInfo = async () => {
  return new Promise<{ title: string; description: string }>((resolve) => {
    setTimeout(() => {
      resolve({
        title: 'Welcome to Task App',
        description:
          'We are a leading organization dedicated to innovation and excellence. Join us to manage your tasks, collaborate with your team, and grow your career.',
      });
    }, 500);
  });
};

export const signupUser = async (username: string, password: string, role: 'employee' | 'admin') => {
  // Disable signups to maintain consistency with JSONPlaceholder users
  throw new Error('Signups are currently disabled to maintain consistency with JSONPlaceholder users.');
};

export const loginUser = async (username: string, password: string) => {
  let users = JSON.parse(localStorage.getItem('users') || '[]');
  if (users.length === 0) {
    users = await getUsers();
  }
  const user = users.find((u: User) => u.username === username && u.password === password);
  if (user) {
    return user;
  }
  throw new Error('Invalid credentials');
};

// Static admin user to preserve existing functionality
const adminUser: User = {
  id: '1',
  username: 'admin',
  password: 'admin123',
  role: 'admin',
};

// Local storage key for user roles (for JSONPlaceholder users)
const USER_ROLES_KEY = 'jsonplaceholder_user_roles';

// Flag to track if JSONPlaceholder users have been fetched
const JSONPLACEHOLDER_FETCHED_KEY = 'jsonplaceholder_fetched';

// Fetch users from JSONPlaceholder and ensure only admin and JSONPlaceholder users are stored
export const getUsers = async () => {
  let users = JSON.parse(localStorage.getItem('users') || '[]');
  const jsonPlaceholderFetched = localStorage.getItem(JSONPLACEHOLDER_FETCHED_KEY);

  // If JSONPlaceholder users haven't been fetched, fetch and merge them
  if (!jsonPlaceholderFetched) {
    try {
      const response = await fetch('https://jsonplaceholder.typicode.com/users');
      if (!response.ok) throw new Error('Failed to fetch users');

      const jsonUsers = await response.json();

      // Retrieve stored roles from localStorage
      const storedRoles: { [key: string]: 'employee' | 'manager' } = JSON.parse(
        localStorage.getItem(USER_ROLES_KEY) || '{}'
      );

      // Map JSONPlaceholder users to our User type
      const existingUsersMap = new Map(users.map((u: User) => [u.id, u]));
      const mappedUsers: User[] = jsonUsers.map((user: any) => {
        const existingUser = existingUsersMap.get(user.id.toString()) as User | undefined;
        return {
          id: user.id.toString(),
          username: user.username,
          password: user.address.zipcode, // Retain the hyphen in the zipcode
          role: storedRoles[user.id.toString()] || 'employee', // Default to 'employee' if no role assigned
          departmentId: existingUser?.departmentId, // Preserve existing departmentId
        };
      });

      // Combine the static admin user with fetched users
      users = [adminUser, ...mappedUsers];
      localStorage.setItem('users', JSON.stringify(users));

      // Mark JSONPlaceholder users as fetched
      localStorage.setItem(JSONPLACEHOLDER_FETCHED_KEY, 'true');
    } catch (error) {
      console.error('Error fetching users from JSONPlaceholder:', error);
      // Fallback to static admin user if the API fails
      if (!users.some((u: User) => u.id === adminUser.id)) {
        users = [adminUser];
        localStorage.setItem('users', JSON.stringify(users));
      }
      localStorage.setItem(JSONPLACEHOLDER_FETCHED_KEY, 'true');
    }
  }

  return users;
};

// Update user role and persist it for JSONPlaceholder users
export const updateUserRole = async (userId: string, newRole: 'employee' | 'manager') => {
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const updatedUsers = users.map((user: User) =>
    user.id === userId ? { ...user, role: newRole } : user
  );
  localStorage.setItem('users', JSON.stringify(updatedUsers));

  // Also update the JSONPlaceholder roles in localStorage if the user is from JSONPlaceholder
  const jsonUsers = await (await fetch('https://jsonplaceholder.typicode.com/users')).json();
  const isJsonPlaceholderUser = jsonUsers.some((user: any) => user.id.toString() === userId);
  if (isJsonPlaceholderUser) {
    const storedRoles: { [key: string]: 'employee' | 'manager' } = JSON.parse(
      localStorage.getItem(USER_ROLES_KEY) || '{}'
    );
    storedRoles[userId] = newRole;
    localStorage.setItem(USER_ROLES_KEY, JSON.stringify(storedRoles));
  }

  return updatedUsers.find((user: User) => user.id === userId);
};

// New functions for departments and tasks
export const createDepartment = async (name: string, managerId: string) => {
  const departments = JSON.parse(localStorage.getItem('departments') || '[]');
  const newDepartment: Department = {
    id: (departments.length + 1).toString(),
    name,
    managerId,
  };
  departments.push(newDepartment);
  localStorage.setItem('departments', JSON.stringify(departments));
  return newDepartment;
};

export const getDepartments = async () => {
  return JSON.parse(localStorage.getItem('departments') || '[]') as Department[];
};

export const assignUserToDepartment = async (userId: string, departmentId: string) => {
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const updatedUsers = users.map((user: User) =>
    user.id === userId ? { ...user, departmentId } : user
  );
  localStorage.setItem('users', JSON.stringify(updatedUsers));
  return updatedUsers.find((user: User) => user.id === userId);
};

export const removeUserFromDepartment = async (userId: string) => {
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const updatedUsers = users.map((user: User) =>
    user.id === userId ? { ...user, departmentId: undefined } : user
  );
  localStorage.setItem('users', JSON.stringify(updatedUsers));
  return updatedUsers.find((user: User) => user.id === userId);
};

export const removeUserFromOrganization = async (userId: string) => {
  const users = JSON.parse(localStorage.getItem('users') || '[]');
  const updatedUsers = users.filter((user: User) => user.id !== userId);
  localStorage.setItem('users', JSON.stringify(updatedUsers));
  // Also remove tasks assigned to this user
  const tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
  const updatedTasks = tasks.filter((task: Task) => task.assignedTo !== userId);
  localStorage.setItem('tasks', JSON.stringify(updatedTasks));
  return updatedUsers;
};

export const createTask = async (
  title: string,
  description: string,
  assignedTo: string,
  departmentId: string,
  createdBy: string,
  dueDate: string = new Date().toISOString(), // Default to current date if not provided
  assignedBy: string = createdBy
) => {
  const tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
  const newTask: Task = {
    id: (tasks.length + 1).toString(),
    title,
    description,
    assignedTo,
    departmentId,
    status: 'pending',
    createdBy,
    dueDate,
    assignedBy,
  };
  tasks.push(newTask);
  localStorage.setItem('tasks', JSON.stringify(tasks));
  return newTask;
};

export const seedTasks = async () => {
  const tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
  if (tasks.length === 0) {
    const defaultTasks: Task[] = [
      {
        id: '1',
        title: 'Complete Project Proposal',
        description: 'Draft the initial project proposal for the new client.',
        assignedTo: '2', // john
        departmentId: '1',
        status: 'pending',
        createdBy: '1', // admin
        dueDate: '2025-04-28T00:00:00Z', // Past due
        assignedBy: '1',
      },
      {
        id: '2',
        title: 'Review Codebase',
        description: 'Perform a code review for the recent changes.',
        assignedTo: '3', // jane
        departmentId: '1',
        status: 'in-progress',
        createdBy: '1',
        dueDate: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(), // Due in 12 hours
        assignedBy: '1',
      },
      {
        id: '3',
        title: 'Update Documentation',
        description: 'Update the project documentation with recent changes.',
        assignedTo: '3', // jane
        departmentId: '1',
        status: 'completed',
        createdBy: '1',
        dueDate: '2025-05-01T00:00:00Z',
        assignedBy: '1',
      },
    ];
    localStorage.setItem('tasks', JSON.stringify(defaultTasks));
    return defaultTasks;
  }
  return tasks;
};

export const getTasks = async () => {
  let tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
  if (tasks.length === 0) {
    tasks = await seedTasks();
  }
  return tasks as Task[];
};

export const updateTask = async (taskId: string, updates: Partial<Task>) => {
  const tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
  const updatedTasks = tasks.map((task: Task) =>
    task.id === taskId ? { ...task, ...updates } : task
  );
  localStorage.setItem('tasks', JSON.stringify(updatedTasks));
  return updatedTasks.find((task: Task) => task.id === taskId);
};

export const deleteTask = async (taskId: string) => {
  const tasks = JSON.parse(localStorage.getItem('tasks') || '[]');
  const updatedTasks = tasks.filter((task: Task) => task.id !== taskId);
  localStorage.setItem('tasks', JSON.stringify(updatedTasks));
  return updatedTasks;
};